import { useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

export const scrollRestorationKey = (viewId: number) => `view-scroll-${viewId}`;

export const useScrollRestoration = (viewId: number) => {
  const { taskId } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const overrideRef = useRef<React.RefObject<HTMLDivElement> | null>(null);
  const restoredRef = useRef(false);
  const previousViewIdRef = useRef<number>(viewId);

  const getScrollElement = () => overrideRef.current?.current ?? containerRef.current;

  // Reset scroll to top when switching between views
  useEffect(() => {
    if (previousViewIdRef.current !== viewId) {
      previousViewIdRef.current = viewId;
      restoredRef.current = true;
      sessionStorage.removeItem(scrollRestorationKey(viewId));
      const container = getScrollElement();
      if (container) {
        container.scrollTop = 0;
        container.scrollLeft = 0;
      }
    }
  }, [viewId]);

  // Reset restoration flag when navigating to a task
  useEffect(() => {
    if (taskId) {
      restoredRef.current = false;
    }
  }, [taskId]);

  // The offset to restore when returning from the task view, or null when there is none.
  // The list reads it on its first render and hands it to the virtualizer as its initial
  // offset, so the restored position is the first one painted. Restoring it afterwards
  // from a double rAF + 150ms retry is what made the list paint at the top first and
  // then jump (DEV-3227).
  const peekRestoreOffset = useCallback((): number | null => {
    if (taskId || restoredRef.current) return null;
    const saved = sessionStorage.getItem(scrollRestorationKey(viewId));
    if (saved === null) return null;
    const offset = parseInt(saved, 10);
    return Number.isFinite(offset) ? offset : null;
  }, [viewId, taskId]);

  // Called once the list has applied the restored offset, so it is not applied again.
  const markRestored = useCallback(() => {
    restoredRef.current = true;
    sessionStorage.removeItem(scrollRestorationKey(viewId));
  }, [viewId]);

  // Save scroll position before task navigation
  const saveScrollBeforeNavigation = useCallback(() => {
    const container = getScrollElement();
    if (container && !taskId) {
      sessionStorage.setItem(scrollRestorationKey(viewId), container.scrollTop.toString());
    }
  }, [viewId, taskId]);

  const registerScrollRef = useCallback((ref: React.RefObject<HTMLDivElement> | null) => {
    overrideRef.current = ref;
  }, []);

  return {
    containerRef,
    saveScrollBeforeNavigation,
    registerScrollRef,
    peekRestoreOffset,
    markRestored,
  };
};
